// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Window chrome — fullscreen and the idle cursor
 * (`docs/archive/terraviz/MULTI_MONITOR_PLAN.md` §3.6).
 *
 * The standard Fullscreen API, which also covers browser-source capture
 * (OBS, vMix). The browser exits fullscreen on Escape without telling
 * anyone who asked for it, so the controller listens for
 * `fullscreenchange` rather than trusting its own last write.
 *
 * AYNI: TerraViz's desktop half — the Tauri window host (decorations,
 * native fullscreen), Ctrl+Q to quit, and the fullscreen preference
 * restored at launch — went with the desktop app (2026-10-06). On the
 * web `requestFullscreen` needs a user gesture, so nothing is restored
 * at boot and nothing is persisted.
 */

import { logger } from '../utils/logger'

/**
 * How long the pointer must hold still before it disappears.
 *
 * Long enough not to fight an operator who is still working, short
 * enough that a cursor abandoned mid-show is gone before anyone
 * photographs it.
 */
export const CURSOR_IDLE_MS = 3000

/**
 * The platform surface: make this window fullscreen, and say whether it
 * is. A seam so the controller below can be driven from a test, and the
 * DOM host below is the one the app wires.
 */
export interface WindowChromeHost {
  setFullscreen(next: boolean): Promise<void>
  /** What the platform currently reports, or `null` when it will not
   *  say — the controller then trusts its own last write. */
  isFullscreen(): boolean | null
}

/**
 * Whether this keystroke is the fullscreen toggle.
 *
 * Pure, because every clause is a bug someone has shipped:
 *
 * - **`repeat`** — holding F11 down delivers a keydown per repeat
 *   interval, and a toggle bound to those flickers the window between
 *   states for as long as the key is held. On a capture surface that is
 *   a strobing signal.
 * - **Modifiers** — Shift+F11 and Ctrl+F11 are bound elsewhere by
 *   desktop environments and by the webview's own devtools; claiming
 *   them silently takes them away.
 * - **`defaultPrevented`** — something upstream already answered this
 *   keystroke, and a second handler acting on it is how one press
 *   produces two actions.
 *
 * **macOS note:** F11 is Mission Control's "show desktop" by default
 * and, on a keyboard with media keys, needs Fn. That is a system
 * binding rather than something to work around here — the Tools menu
 * entry is the reliable path on a Mac, which is why the feature does
 * not ship as a shortcut alone.
 */
export function isFullscreenHotkey(ev: KeyboardEvent): boolean {
  return (
    ev.key === 'F11' &&
    !ev.repeat &&
    !ev.altKey &&
    !ev.ctrlKey &&
    !ev.metaKey &&
    !ev.shiftKey &&
    !ev.defaultPrevented
  )
}

export interface FullscreenController {
  /** What this window is in, as far as anyone can tell. */
  isFullscreen(): boolean
  /** Go there. Resolves once the platform has been asked. */
  set(next: boolean): Promise<void>
  toggle(): Promise<boolean>
  /** Called whenever the state changes, including when the platform
   *  changed it without being asked (Escape on the web). */
  onChange(listener: (fullscreen: boolean) => void): () => void
  dispose(): void
}

export interface FullscreenControllerOptions {
  host: WindowChromeHost
  /** Where the F11 handler and the web's `fullscreenchange` are
   *  attached. Injectable so a test needs no page. */
  target?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null
}

/**
 * Own one window's fullscreen state.
 *
 * Holds its own view of the state rather than asking the platform every
 * time, because the DOM's answer is only meaningful once a request has
 * resolved. The held value is corrected
 * by whatever the host *can* report, so an Escape-exit on the web does
 * not leave the Tools menu claiming fullscreen.
 */
export function createFullscreenController(
  options: FullscreenControllerOptions,
): FullscreenController {
  const { host } = options
  const target = options.target ?? (typeof window !== 'undefined' ? window : null)
  const listeners = new Set<(fullscreen: boolean) => void>()
  let current = false

  const announce = (next: boolean): void => {
    if (next === current) return
    current = next
    for (const listener of listeners) {
      // Isolated: a Tools-menu button that throws while updating its
      // own label must not stop the cursor hider from being told, and
      // must not unwind into a keydown handler.
      try {
        listener(next)
      } catch (err) {
        logger.error('[windowChrome] fullscreen listener threw:', err)
      }
    }
  }

  const apply = async (next: boolean): Promise<void> => {
    try {
      await host.setFullscreen(next)
    } catch (err) {
      // The platform refused. Report what it actually is rather than
      // what was asked for, so the menu does not claim a state the
      // window is not in.
      logger.warn('[windowChrome] could not change fullscreen:', err)
      announce(host.isFullscreen() ?? current)
      return
    }
    announce(next)
  }

  // The browser exits fullscreen on Escape without consulting whoever
  // asked for it, so the held value has to be corrected from the event
  // rather than trusted.
  const onPlatformChange = (): void => {
    const reported = host.isFullscreen()
    if (reported !== null) announce(reported)
  }
  target?.addEventListener('fullscreenchange', onPlatformChange)

  const onKeyDown = (ev: Event): void => {
    if (!isFullscreenHotkey(ev as KeyboardEvent)) return
    // Claimed only once we know we are acting on it, so a modified or
    // repeated F11 still reaches whatever else wanted it.
    ;(ev as KeyboardEvent).preventDefault()
    void apply(!current)
  }
  target?.addEventListener('keydown', onKeyDown)

  return {
    isFullscreen: () => current,
    set: apply,
    async toggle() {
      await apply(!current)
      return current
    },
    onChange(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    dispose() {
      listeners.clear()
      target?.removeEventListener('keydown', onKeyDown)
      target?.removeEventListener('fullscreenchange', onPlatformChange)
    },
  }
}

/** The host the app wires: the standard Fullscreen API. */
export function createDomChromeHost(doc: Document = document): WindowChromeHost {
  return {
    async setFullscreen(next) {
      if (next) await doc.documentElement.requestFullscreen()
      else if (doc.fullscreenElement) await doc.exitFullscreen()
    },
    isFullscreen: () => doc.fullscreenElement !== null,
  }
}

export interface IdleCursor {
  /** Start or stop watching. The control window turns this on when it
   *  goes fullscreen and off when it comes back. */
  setActive(active: boolean): void
  dispose(): void
}

export interface IdleCursorOptions {
  /** Toggled on the root element; the stylesheet does the hiding. */
  root?: { classList: Pick<DOMTokenList, 'add' | 'remove'> } | null
  target?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null
  idleMs?: number
  setTimer?: (fn: () => void, ms: number) => number
  clearTimer?: (id: number) => void
}

/** The class the stylesheet keys the hidden cursor off. */
export const CURSOR_IDLE_CLASS = 'cursor-idle'

/**
 * Hide the pointer after it holds still, while active.
 *
 * Only while fullscreen, because that is the only time the control
 * window is a capture surface — hiding the cursor of a windowed app an
 * operator is still driving would be a bug, not a feature.
 *
 * The hidden state is a class rather than an inline style so the rule
 * lives in CSS, where the `*` selector it needs is expressible: a
 * `cursor: none` on the root alone is overridden by every button's own
 * `cursor: pointer`, which is most of the chrome this is trying to
 * clear from the signal.
 *
 * Any pointer movement reveals it again and restarts the clock. The
 * listener is `pointermove` rather than `mousemove` so a pen or touch
 * contact counts, and it is **passive**: this never calls
 * `preventDefault`, and saying so up front stops the browser blocking
 * scroll on the chance that it might.
 */
export function createIdleCursor(options: IdleCursorOptions = {}): IdleCursor {
  const root =
    options.root ?? (typeof document !== 'undefined' ? document.documentElement : null)
  const target = options.target ?? (typeof window !== 'undefined' ? window : null)
  const idleMs = options.idleMs ?? CURSOR_IDLE_MS
  const setTimer =
    options.setTimer ?? ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number)
  const clearTimer = options.clearTimer ?? (id => globalThis.clearTimeout(id))

  let timer: number | null = null
  let active = false

  const show = (): void => {
    root?.classList.remove(CURSOR_IDLE_CLASS)
  }

  const arm = (): void => {
    if (timer !== null) clearTimer(timer)
    timer = setTimer(() => {
      timer = null
      // Re-checked at fire time, not only at arm time: `setActive(false)`
      // between the two would otherwise hide the cursor of a window that
      // has already left fullscreen.
      if (active) root?.classList.add(CURSOR_IDLE_CLASS)
    }, idleMs)
  }

  const onMove = (): void => {
    if (!active) return
    show()
    arm()
  }
  target?.addEventListener('pointermove', onMove, { passive: true })

  return {
    setActive(next) {
      if (next === active) return
      active = next
      if (active) {
        // Armed immediately rather than on the first movement: an
        // operator who clicks Fullscreen and then takes their hand off
        // the mouse never generates one.
        arm()
      } else {
        if (timer !== null) clearTimer(timer)
        timer = null
        show()
      }
    },
    dispose() {
      if (timer !== null) clearTimer(timer)
      timer = null
      show()
      target?.removeEventListener('pointermove', onMove)
    },
  }
}
